import { useState, useCallback, useRef, useEffect } from 'react';
import client from '../api/client';
import { notifyCacheRefreshed, subscribeCacheRefreshed } from '../utils/cacheEvents';

function axiosErrorMessage(err) {
  const d = err.response?.data;
  if (typeof d === 'string') return d;
  if (d?.error && typeof d.error === 'string') return d.error;
  if (d?.title && typeof d.title === 'string') return d.title;
  if (err.code === 'ECONNABORTED') return 'Request timed out';
  const status = err.response?.status;
  if (status === 504) return 'Gateway timeout (upstream or proxy)';
  if (status === 502 || status === 503) return 'Service temporarily unavailable';
  return err.message || 'Request failed';
}

export function useCache() {
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  /** VitalEngine/cache refresh only — do not tie Salesforce facilities sync to this. */
  const [dataRefreshing, setDataRefreshing] = useState(false);
  const [facilitiesRefreshing, setFacilitiesRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [error, setError] = useState(null);

  // Shared cancel flag for the per-type for-loop in refreshData(). useState is
  // unreliable inside a long-running async loop because of stale closures, so
  // we read/write through a ref. cancelRefresh() flips this to true; the loop
  // checks it before each iteration and breaks immediately, instead of moving
  // on to the next data type after the user clicks Stop.
  const cancelledRef = useRef(false);
  const manualRefreshActiveRef = useRef(false);

  const fetchCacheStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await client.get(`/api/cache/status`);
      if (response.data.success) {
        setCacheStatus(response.data);
      }
    } catch (err) {
      console.error('Error fetching cache status:', err);
      setError('Failed to load cache status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload status cards when any tab finishes a cache refresh.
  useEffect(() => subscribeCacheRefreshed(() => { fetchCacheStatus(); }), [fetchCacheStatus]);

  // Mirror scheduled (background) refresh progress onto the Data Sources cards.
  useEffect(() => {
    let wasScheduled = false;
    const poll = async () => {
      if (manualRefreshActiveRef.current) return;
      try {
        const res = await client.get('/api/cache/refresh-status');
        const inProgress = !!res.data?.inProgress;
        const scheduled = res.data?.triggeredBy === 'scheduled';
        if (inProgress && scheduled) {
          wasScheduled = true;
          const phase = res.data?.phase;
          setDataRefreshing(phase !== 'facilities');
          setFacilitiesRefreshing(phase === 'facilities');
          setRefreshProgress({
            scheduledMode: true,
            currentType: res.data?.currentDataType,
            current: res.data?.dataTypesCompleted ?? 0,
            total: res.data?.dataTypesTotal ?? 0,
          });
        } else if (wasScheduled && !inProgress) {
          wasScheduled = false;
          setDataRefreshing(false);
          setFacilitiesRefreshing(false);
          setRefreshProgress(null);
          await fetchCacheStatus();
        }
      } catch {
        /* ignore */
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [fetchCacheStatus]);

  const refreshData = useCallback(async (dataTypes, startDate, endDate, mode = 'smart') => {
    try {
      manualRefreshActiveRef.current = true;
      setDataRefreshing(true);
      setError(null);
      cancelledRef.current = false;
      const allResults = [];
      /** @type {Record<string, { success: boolean, error?: string, recordCount?: number, actualStartDate?: string, actualEndDate?: string }>} */
      let resultsByType = {};

      const bumpProgress = (i, currentType, partialMap) => {
        setRefreshProgress({
          requestedStartDate: startDate,
          requestedEndDate: endDate,
          refreshMode: mode,
          current: i,
          total: dataTypes.length,
          currentType,
          resultsByType: partialMap,
          percent:
            currentType !== null && dataTypes.length > 0
              ? Math.min(99, Math.round(((i + 0.35) / dataTypes.length) * 100))
              : 100
        });
      };

      for (let i = 0; i < dataTypes.length; i++) {
        // Cancel check #1: bail before kicking off the next type. This is the
        // one that fixes the "I cancelled and it kept going" bug — without it,
        // pressing Stop only cancels the current backend call and the loop
        // happily POSTs the next data type.
        if (cancelledRef.current) break;

        const dt = dataTypes[i];
        bumpProgress(i, dt, { ...resultsByType });

        try {
          const response = await client.post(`/api/cache/refresh`, {
            dataTypes: [dt],
            startDate,
            endDate,
            mode
          });
          const rows = response.data?.results || [];
          for (const r of rows) {
            const key = r.dataType || r.DataType;
            if (!key) continue;
            allResults.push({
              dataType: key,
              success: r.success ?? r.Success,
              recordCount: r.recordCount ?? r.RecordCount,
              error: r.error ?? r.Error,
              actualStartDate: r.actualStartDate ?? r.ActualStartDate,
              actualEndDate: r.actualEndDate ?? r.ActualEndDate
            });
            resultsByType = {
              ...resultsByType,
              [key]: {
                success: r.success ?? r.Success,
                error: r.error ?? r.Error ?? null,
                recordCount: r.recordCount ?? r.RecordCount,
                actualStartDate: r.actualStartDate ?? r.ActualStartDate ?? null,
                actualEndDate: r.actualEndDate ?? r.ActualEndDate ?? null
              }
            };
          }
          bumpProgress(i, dt, { ...resultsByType });
        } catch (err) {
          const msg = axiosErrorMessage(err);
          console.error('Error refreshing data type', dt, err);
          allResults.push({ dataType: dt, success: false, error: msg });
          resultsByType = {
            ...resultsByType,
            [dt]: { success: false, error: msg }
          };
          bumpProgress(i, dt, { ...resultsByType });
        }

        // Cancel check #2: defensive — if cancel was clicked while the await
        // above was in flight (the common case: user hits Stop while a long
        // backend call is running), break here too instead of looping again.
        if (cancelledRef.current) break;
      }

      setRefreshProgress({
        requestedStartDate: startDate,
        requestedEndDate: endDate,
        refreshMode: mode,
        current: dataTypes.length,
        total: dataTypes.length,
        currentType: null,
        resultsByType: { ...resultsByType },
        percent: 100
      });

      // `/api/cache/status` does a COUNT + MIN/MAX per data type on prod SQLite and can
      // take many seconds.  We already show "Complete" in the progress label, but
      // `dataRefreshing` used to stay true until this await finished, which left
      // "Refresh Data" disabled and looked like the run never really finished.
      setDataRefreshing(false);

      await fetchCacheStatus();

      setTimeout(() => setRefreshProgress(null), 4000);

      const success = allResults.every(r => r.success);
      // Tell pages that depend on the cache (Home dashboard, etc.) to refetch.
      // Fire even on partial success so consumers see whatever did land.
      if (allResults.some(r => r.success)) notifyCacheRefreshed('data');
      return { success, results: allResults };
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err.response?.data?.error || 'Failed to refresh data');
      return null;
    } finally {
      manualRefreshActiveRef.current = false;
      setDataRefreshing(false);
    }
  }, [fetchCacheStatus]);

  const refreshFacilities = useCallback(async () => {
    try {
      manualRefreshActiveRef.current = true;
      setFacilitiesRefreshing(true);
      setError(null);
      const response = await client.post(`/api/cache/refresh-facilities`);
      await fetchCacheStatus();
      if (response.data?.success !== false) notifyCacheRefreshed('facilities');
      return response.data;
    } catch (err) {
      console.error('Error refreshing facilities:', err);
      setError(err.response?.data?.error || 'Failed to refresh facilities');
      return null;
    } finally {
      manualRefreshActiveRef.current = false;
      setFacilitiesRefreshing(false);
    }
  }, [fetchCacheStatus]);

  const getVeFacilityDirectory = useCallback(async () => {
    const response = await client.get('/api/cache/ve-facility-directory');
    return response.data;
  }, []);

  const getCachedData = useCallback(async (type, startDate, endDate) => {
    try {
      const params = { type };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await client.get(`/api/cache/data`, { params });
      return response.data;
    } catch (err) {
      console.error('Error fetching cached data:', err);
      throw err;
    }
  }, []);

  const getRawData = useCallback(async (type, startDate, endDate) => {
    try {
      const response = await client.get(`/api/cache/raw`, {
        params: { type, startDate, endDate }
      });
      return response.data;
    } catch (err) {
      console.error('Error fetching raw data:', err);
      throw err;
    }
  }, []);

  const getRawPhiData = useCallback(async (type, startDate, endDate, {
    recordIds = [],
    facilityIds = [],
    direction = 'inbound',
  } = {}) => {
    try {
      const response = await client.post('/api/cache/raw/phi', {
        type,
        startDate,
        endDate,
        recordIds,
        facilityIds,
        direction,
      });
      return response.data;
    } catch (err) {
      console.error('Error fetching raw PHI data:', err);
      throw err;
    }
  }, []);

  const getCachedFacilities = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/facilities`);
      return response.data;
    } catch (err) {
      console.error('Error fetching cached facilities:', err);
      throw err;
    }
  }, []);

  const getCachedFacilityHierarchy = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/facilities/hierarchy`);
      return response.data;
    } catch (err) {
      console.error('Error fetching cached facility hierarchy:', err);
      throw err;
    }
  }, []);

  const getPhiConfig = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/phi-config`);
      return response.data;
    } catch (err) {
      console.error('Error fetching PHI config:', err);
      throw err;
    }
  }, []);

  const updatePhiConfig = useCallback(async (configs) => {
    try {
      const response = await client.put(`/api/cache/phi-config`, configs);
      return response.data;
    } catch (err) {
      console.error('Error updating PHI config:', err);
      throw err;
    }
  }, []);

  const deletePhiConfig = useCallback(async (id) => {
    try {
      const response = await client.delete(`/api/cache/phi-config/${id}`);
      return response.data;
    } catch (err) {
      console.error('Error deleting PHI config:', err);
      throw err;
    }
  }, []);

  const getSavedFilters = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/filters`);
      return response.data;
    } catch (err) {
      console.error('Error fetching saved filters:', err);
      throw err;
    }
  }, []);

  const createFilter = useCallback(async (name, facilityIds) => {
    try {
      const response = await client.post(`/api/cache/filters`, { name, facilityIds });
      return response.data;
    } catch (err) {
      console.error('Error creating filter:', err);
      throw err;
    }
  }, []);

  const updateFilter = useCallback(async (id, name, facilityIds) => {
    try {
      const response = await client.put(`/api/cache/filters/${id}`, { name, facilityIds });
      return response.data;
    } catch (err) {
      console.error('Error updating filter:', err);
      throw err;
    }
  }, []);

  const deleteFilter = useCallback(async (id) => {
    try {
      const response = await client.delete(`/api/cache/filters/${id}`);
      return response.data;
    } catch (err) {
      console.error('Error deleting filter:', err);
      throw err;
    }
  }, []);

  const getDrillDownFields = useCallback(async (dataType) => {
    try {
      const response = await client.get(`/api/cache/drilldown-fields/${dataType}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching drilldown fields:', err);
      throw err;
    }
  }, []);

  const getDrillDownConfig = useCallback(async (dataType) => {
    try {
      const response = await client.get(`/api/cache/drilldown-config/${dataType}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching drilldown config:', err);
      throw err;
    }
  }, []);

  const saveDrillDownConfig = useCallback(async (dataType, columns) => {
    try {
      const response = await client.put(`/api/cache/drilldown-config/${dataType}`, columns);
      return response.data;
    } catch (err) {
      console.error('Error saving drilldown config:', err);
      throw err;
    }
  }, []);

  const getFreshness = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/freshness`);
      return response.data;
    } catch (err) {
      console.error('Error fetching freshness:', err);
      throw err;
    }
  }, []);

  /**
   * Upload a locally-built SQLite snapshot (from scripts/build-cache-bootstrap.ps1)
   * to POST /api/cache/import on the server. Used to bypass EFS write latency
   * for the initial multi-year backfill: pull records on a fast SSD locally,
   * then ship the .db file in a single shot. See CACHE-BOOTSTRAP.md.
   *
   * @param {File} file - the .db snapshot file from <input type="file">
   * @param {{ includeFacilities?: boolean, createBackup?: boolean, onUploadProgress?: (pct:number)=>void }} [opts]
   */
  const importCacheSnapshot = useCallback(async (file, opts = {}) => {
    const { includeFacilities = false, createBackup = true, onUploadProgress } = opts;
    const form = new FormData();
    form.append('file', file);
    form.append('includeFacilities', String(!!includeFacilities));
    form.append('createBackup', String(!!createBackup));

    const response = await client.post('/api/cache/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // The 1h global timeout from client.js handles the server-side import
      // step; the upload itself is bounded by nginx client_body_timeout.
      onUploadProgress: (evt) => {
        if (!evt.total || !onUploadProgress) return;
        onUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    });
    // Refresh the cache status so the cards reflect the imported rows.
    await fetchCacheStatus();
    notifyCacheRefreshed('data');
    return response.data;
  }, [fetchCacheStatus]);

  const cancelRefresh = useCallback(async () => {
    // Set the flag SYNCHRONOUSLY before awaiting anything so the for-loop in
    // refreshData() picks it up on its next iteration check, even if the
    // backend cancel call below is still in flight (or fails outright).
    cancelledRef.current = true;
    try {
      const response = await client.post(`/api/cache/cancel-refresh`);
      if (response.data?.success) {
        setDataRefreshing(false);
        setRefreshProgress(null);
        setError('Refresh cancelled');
      }
      return response.data;
    } catch (err) {
      console.error('Error cancelling refresh:', err);
      throw err;
    }
  }, []);

  const getRefreshStatus = useCallback(async () => {
    try {
      const response = await client.get(`/api/cache/refresh-status`);
      return response.data;
    } catch (err) {
      console.error('Error fetching refresh status:', err);
      throw err;
    }
  }, []);

  return {
    cacheStatus,
    loading,
    /** @deprecated Prefer dataRefreshing — this is VitalEngine/cache only */
    refreshing: dataRefreshing,
    dataRefreshing,
    facilitiesRefreshing,
    refreshProgress,
    error,
    fetchCacheStatus,
    refreshData,
    refreshFacilities,
    importCacheSnapshot,
    cancelRefresh,
    getRefreshStatus,
    getCachedData,
    getVeFacilityDirectory,
    getRawData,
    getRawPhiData,
    getCachedFacilities,
    getCachedFacilityHierarchy,
    getPhiConfig,
    updatePhiConfig,
    deletePhiConfig,
    getSavedFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    getDrillDownFields,
    getDrillDownConfig,
    saveDrillDownConfig,
    getFreshness
  };
}
